// u01 — housewarming variant
//
// "Karimovlar oilasi" — first-home celebration on 08.06.2026 in Tashkent.
// The brief is warm, domestic, family + neighbours, traditional Uzbek
// house-blessing. Cinnamon, cream, warm light. We open on a hero with the
// new home's interior photo behind a cream wash, a Lora italic headline
// (Karimovlar yangi uyiga sizni taklif qiladi) and a short warm welcome.
//
// Signature element: the brick-house silhouette in the hero has three
// smoke puffs rising gently from the chimney — each puff loops translateY
// + opacity (4s ease-in-out infinite, staggered offsets so they form a
// continuous chain). prefers-reduced-motion freezes them in their static
// composition.
//
// Structure:
//   1. Hero — interior photo behind a cream wash, brick-house SVG with
//      gently rising smoke, headline, welcome subtitle, date · venue keyline
//   2. Countdown
//   3. Story — bizning yangi uy / Birinchi yangi uyimiz (warm Uzbek copy)
//   4. UNIQUE — Open House Tour: 4 rooms (mehmonxona, oshxona, hovli,
//      terrasa), each with a small icon, name, and one-liner
//   5. UNIQUE — Hadiyalar (Gifts wishlist): 5 useful items each with
//      suggested price range
//   6. Gallery
//   7. Schedule (5 events, 11:00 → 18:00)
//   8. Map (Yunusobod tumani · Karimovlar mahallasi · 14-uy)
//   9. RSVP — "Yangi uyimizga keling"
//  10. Share
//  11. Footer
//
// Sound: 4 triggers — `chime` once when RSVP is confirmed yes, `tap` on
// each room hover/focus in the tour grid, `click` on every accent button
// (gifts cards, music toggle), `swoosh` once on each Reveal first show.
// All gated through useSound's persisted preference and prefers-reduced-
// motion.
//
// Music: a small floating "Uy musiqasi" toggle in the bottom-right corner.
// We don't ship an audio file — when on, a tiny Web Audio loop plays a
// warm 5-note phrase on a sine oscillator (cinnamon harmonics) so the
// invitation feels like a gentle rabob without a network hit.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion hook ──────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-driven reveal ───────────────────────────────────

interface RevealProps {
  children: ReactNode;
  delay?: number;
  reduce: boolean;
  /** Fired exactly once when the element first crosses the threshold. */
  onShow?: () => void;
}

function Reveal({ children, delay = 0, reduce, onShow }: RevealProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);
  const fired = useRef(false);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      if (!fired.current) {
        fired.current = true;
        onShow?.();
      }
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            if (!fired.current) {
              fired.current = true;
              onShow?.();
            }
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, onShow]);

  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(16px)",
        transition: reduce
          ? "none"
          : `opacity 1.0s ease-out ${delay}ms, transform 1.0s ease-out ${delay}ms`,
        willChange: reduce ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

// ─── SIGNATURE — brick house with rising smoke ───────────────────────────
//
// Three smoke circles rise slowly from the chimney. Each follows the same
// keyframe (translateY + fade), but with staggered delays so puffs leave
// the chimney one after another. prefers-reduced-motion freezes them in
// their default static composition (matching the layout's preview card).

function HouseWithSmoke({
  warmDark,
  cinnamon,
  cream,
  size = 180,
  reduce,
}: {
  warmDark: string;
  cinnamon: string;
  cream: string;
  size?: number;
  reduce: boolean;
}) {
  const smokeStyle = (delay: number) =>
    reduce
      ? undefined
      : {
          animation: `uySmokeRise 4s ease-in-out ${delay}s infinite`,
          transformOrigin: "center" as const,
        };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      style={{ display: "block", overflow: "visible" }}
      aria-hidden
    >
      {/* Smoke puffs — three circles rising from the chimney */}
      <g fill={warmDark}>
        <circle cx="45" cy="18" r="2.6" opacity="0.55" style={smokeStyle(0)} />
        <circle cx="48" cy="14" r="2.0" opacity="0.40" style={smokeStyle(0.9)} />
        <circle cx="46" cy="10" r="1.6" opacity="0.28" style={smokeStyle(1.8)} />
      </g>
      {/* Chimney */}
      <rect x="42" y="20" width="6" height="9" fill={warmDark} />
      {/* Roof */}
      <path d="M 8 32 L 32 16 L 56 32 Z" fill={cinnamon} />
      <path d="M 8 32 L 56 32 L 53 33 L 11 33 Z" fill={warmDark} opacity="0.25" />
      {/* House body */}
      <rect x="14" y="32" width="36" height="22" fill={warmDark} />
      {/* Door — warmly lit */}
      <rect x="28" y="40" width="8" height="14" fill={cinnamon} />
      <circle cx="34" cy="47" r="0.7" fill={cream} />
      {/* Windows — glowing */}
      <rect x="18" y="36" width="6" height="6" fill={cream} opacity="0.9" />
      <rect x="40" y="36" width="6" height="6" fill={cream} opacity="0.9" />
      <line x1="21" y1="36" x2="21" y2="42" stroke={warmDark} strokeWidth="0.5" />
      <line x1="18" y1="39" x2="24" y2="39" stroke={warmDark} strokeWidth="0.5" />
      <line x1="43" y1="36" x2="43" y2="42" stroke={warmDark} strokeWidth="0.5" />
      <line x1="40" y1="39" x2="46" y2="39" stroke={warmDark} strokeWidth="0.5" />
      {/* Ground line */}
      <line x1="6" y1="54" x2="58" y2="54" stroke={warmDark} strokeWidth="0.8" />
    </svg>
  );
}

// ─── Tiny SVG icons for the room tour ────────────────────────────────────

function RoomIcon({ kind, color }: { kind: "guest" | "kitchen" | "yard" | "terrace"; color: string }) {
  const common = { stroke: color, strokeWidth: 1.4, fill: "none", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  return (
    <svg width="36" height="36" viewBox="0 0 32 32" aria-hidden style={{ display: "block" }}>
      {kind === "guest" && (
        <g {...common}>
          {/* Cushioned divan + tea-pot suggestion */}
          <rect x="4" y="14" width="24" height="10" rx="2" />
          <path d="M 6 14 L 6 11 Q 6 9 8 9 L 24 9 Q 26 9 26 11 L 26 14" />
          <circle cx="16" cy="6" r="1.6" />
          <path d="M 13 8 Q 16 5 19 8" />
        </g>
      )}
      {kind === "kitchen" && (
        <g {...common}>
          {/* Pot with steam — dasturxon hint */}
          <path d="M 7 18 L 7 24 Q 7 26 9 26 L 23 26 Q 25 26 25 24 L 25 18 Z" />
          <line x1="5" y1="18" x2="27" y2="18" />
          <path d="M 12 14 Q 13 11 12 8" />
          <path d="M 16 14 Q 17 10 16 6" />
          <path d="M 20 14 Q 21 11 20 8" />
        </g>
      )}
      {kind === "yard" && (
        <g {...common}>
          {/* Tree (tut) */}
          <path d="M 16 26 L 16 16" />
          <circle cx="16" cy="12" r="6" />
          <circle cx="11" cy="14" r="4" />
          <circle cx="21" cy="14" r="4" />
          <line x1="6" y1="26" x2="26" y2="26" />
        </g>
      )}
      {kind === "terrace" && (
        <g {...common}>
          {/* Railing + sun/moon over horizon */}
          <line x1="4" y1="22" x2="28" y2="22" />
          <line x1="6" y1="22" x2="6" y2="28" />
          <line x1="11" y1="22" x2="11" y2="28" />
          <line x1="16" y1="22" x2="16" y2="28" />
          <line x1="21" y1="22" x2="21" y2="28" />
          <line x1="26" y1="22" x2="26" y2="28" />
          <circle cx="22" cy="10" r="3.5" />
        </g>
      )}
    </svg>
  );
}

// ─── Hearth music — synthesised warm 5-note loop ─────────────────────────
//
// We don't ship an audio file. When the user enables, a small interval
// schedules a 5-note warm pentatonic phrase (C-E-G-A-C, an octave higher)
// on a sine oscillator with a slow attack. It's quiet, on-brand, and gated
// by the same useSound preference (so muting kills it).

const HEARTH_NOTES = [261.63, 329.63, 392.0, 440.0, 523.25];

function useHearthTheme(enabled: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (ctxRef.current && ctxRef.current.state === "running") {
        void ctxRef.current.suspend().catch(() => undefined);
      }
      return;
    }

    let cancelled = false;
    const start = async () => {
      if (!ctxRef.current) {
        const Ctor =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!Ctor) return;
        try {
          ctxRef.current = new Ctor();
        } catch {
          return;
        }
      }
      const c = ctxRef.current;
      if (!c) return;
      if (c.state === "suspended") {
        try {
          await c.resume();
        } catch {
          return;
        }
      }
      if (cancelled) return;

      const playPhrase = () => {
        const ctx = ctxRef.current;
        if (!ctx || ctx.state !== "running") return;
        const base = ctx.currentTime;
        HEARTH_NOTES.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const g = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, base + i * 0.5);
          // Warm soft envelope.
          const t0 = base + i * 0.5;
          const peak = 0.045;
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.exponentialRampToValueAtTime(peak, t0 + 0.05);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.45);
          osc.connect(g);
          g.connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.5);
          osc.onended = () => {
            try {
              osc.disconnect();
              g.disconnect();
            } catch {
              /* noop */
            }
          };
        });
      };

      playPhrase();
      timerRef.current = window.setInterval(playPhrase, 4500);
    };
    void start();

    return () => {
      cancelled = true;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled]);

  // Final cleanup — close the context when the variant unmounts entirely.
  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
      if (ctxRef.current) {
        try {
          void ctxRef.current.close();
        } catch {
          /* noop */
        }
      }
    };
  }, []);
}

// ─── Tour data — 4 rooms, indexed via the dictionary ─────────────────────

const ROOMS: Array<{ key: "guest" | "kitchen" | "yard" | "terrace" }> = [
  { key: "guest" },
  { key: "kitchen" },
  { key: "yard" },
  { key: "terrace" },
];

// ─── Gifts data — 5 items, indexed via the dictionary ────────────────────

const GIFT_COUNT = 5;

// ─── Component ────────────────────────────────────────────────────────────

export function HousewarmingVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const tt = useT();
  const { play, enabled: soundEnabled } = useSound();

  // Music toggle — independent from the global sound preference for UI
  // accents. We default OFF so guests aren't surprised on first paint.
  const [music, setMusic] = useState<boolean>(false);
  useHearthTheme(music && soundEnabled);

  const toggleMusic = useCallback(() => {
    setMusic((prev) => {
      const next = !prev;
      // Sound trigger #3 — music toggle click.
      play(next ? "music-on" : "music-off");
      return next;
    });
  }, [play]);

  // Wrap RSVP setter so a "yes" plays a single warm chime.
  const wrappedRsvp = {
    ...ctx.rsvp,
    setRsvp: (v: string | null) => {
      const before = ctx.rsvp.rsvp;
      ctx.rsvp.setRsvp(v);
      // Sound trigger #1 — chime on first yes.
      if (v === "yes" && before !== "yes") play("chime");
    },
  };

  // Palette — read from template colors so a future re-skin flows through.
  const cream = t.bg;
  const warmDark = t.ink;
  const cinnamon = t.accent;
  const heroPhoto = t.photos[0];

  const swooshOnShow = useCallback(() => {
    // Sound trigger #4 — soft swoosh on each section reveal.
    play("swoosh");
  }, [play]);

  return (
    <div className="v-u01">
      {/* ── Local CSS: smoke loop + responsive lockdown ───────────── */}
      <style>{`
        @keyframes uySmokeRise {
          0%   { transform: translateY(0)    scale(1);    opacity: 0; }
          20%  { opacity: 0.55; }
          60%  { transform: translateY(-12px) scale(1.18); opacity: 0.32; }
          100% { transform: translateY(-22px) scale(1.4);  opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .uy-smoke {
            animation: none !important;
          }
        }

        .uy-room-card {
          transition: background-color .25s ease-out, transform .25s ease-out, box-shadow .25s ease-out;
        }
        .uy-room-card:hover,
        .uy-room-card:focus-within {
          background: ${cinnamon}14;
          transform: translateY(-2px);
          box-shadow: 0 8px 18px ${warmDark}1f;
        }
        @media (prefers-reduced-motion: reduce) {
          .uy-room-card:hover,
          .uy-room-card:focus-within { transform: none; }
        }

        .uy-gift-row {
          transition: background-color .25s ease-out, transform .25s ease-out;
        }
        .uy-gift-row:hover,
        .uy-gift-row:focus-within {
          background: ${cinnamon}10;
          transform: translateX(2px);
        }
        @media (prefers-reduced-motion: reduce) {
          .uy-gift-row:hover,
          .uy-gift-row:focus-within { transform: none; }
        }

        .uy-music-btn {
          transition: transform .25s ease-out, box-shadow .25s ease-out;
        }
        .uy-music-btn:hover { transform: translateY(-1px); }
        @media (prefers-reduced-motion: reduce) {
          .uy-music-btn:hover { transform: none; }
        }

        .v-u01 a:focus-visible,
        .v-u01 button:focus-visible {
          outline: 2px solid ${cinnamon};
          outline-offset: 2px;
        }

        @media (max-width: 768px) {
          .v-u01 button, .v-u01 a { min-height: 44px; }
          .uy-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .uy-hero-eyebrow { font-size: 9px !important; letter-spacing: 0.32em !important; }
          .uy-hero-headline { font-size: clamp(36px, 12vw, 96px) !important; }
          .uy-hero-meta { font-size: 11px !important; letter-spacing: 0.28em !important; }
          .uy-tour-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .uy-gifts-grid { grid-template-columns: 1fr !important; }
          .uy-music-toggle {
            right: 16px !important;
            bottom: 16px !important;
          }
          .v-u01 section { padding-left: 18px !important; padding-right: 18px !important; }
        }
      `}</style>

      {/* ─── HERO ──────────────────────────────────────────────────── */}
      <section
        className="uy-hero"
        style={{
          minHeight: "100vh",
          padding: "120px 32px 96px",
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          background: cream,
          color: warmDark,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Interior photo — ghosted under the headline */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: heroPhoto ? `url("${heroPhoto}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.28,
            filter: "saturate(0.9)",
          }}
        />
        {/* Cream wash so type stays legible at any photo */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${cream}f5 0%, ${cream}cc 35%, ${cream}f5 100%)`,
          }}
        />
        {/* Inset hairline frame */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: "min(40px, 6vw)",
            border: `1px solid ${warmDark}22`,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", maxWidth: 880, margin: "0 auto" }}>
          {/* Eyebrow */}
          <div
            className="uy-hero-eyebrow"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: cinnamon,
              marginBottom: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 14,
            }}
          >
            <span style={{ width: 28, height: 1, background: cinnamon }} />
            {tt("variant.u01.hero.eyebrow")}
            <span style={{ width: 28, height: 1, background: cinnamon }} />
          </div>

          {/* Brick-house silhouette with smoke */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <HouseWithSmoke
              warmDark={warmDark}
              cinnamon={cinnamon}
              cream={cream}
              size={144}
              reduce={reduce}
            />
          </div>

          {/* Headline */}
          <h1
            className="uy-hero-headline"
            style={{
              fontFamily: t.font || "var(--font-display)",
              fontStyle: "italic",
              fontSize: "clamp(40px, 7vw, 84px)",
              lineHeight: 1.1,
              fontWeight: 400,
              letterSpacing: "-0.005em",
              margin: 0,
              color: warmDark,
            }}
          >
            {tt("variant.u01.hero.headline")}
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 18,
              lineHeight: 1.7,
              fontStyle: "italic",
              color: `${warmDark}cc`,
              maxWidth: 560,
              margin: "24px auto 0",
            }}
          >
            {tt("variant.u01.hero.subtitle")}
          </p>

          {/* Date · venue keyline */}
          <div style={{ margin: "44px auto 0", maxWidth: 360 }}>
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: cinnamon, opacity: 0.55, marginBottom: 14 }}
            />
            <div
              className="uy-hero-meta"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.34em",
                color: warmDark,
                fontFeatureSettings: '"tnum" 1, "lnum" 1',
              }}
            >
              {t.date.replace(/\./g, " · ")} · {t.venue.toUpperCase()}
            </div>
            <span
              aria-hidden
              style={{ display: "block", height: 1, background: cinnamon, opacity: 0.55, marginTop: 14 }}
            />
          </div>
        </div>
      </section>

      <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <StorySection
          t={t}
          eyebrow={tt("variant.u01.story.eyebrow")}
          title={tt("variant.u01.story.title")}
          body={tt("variant.u01.story.body")}
        />
      </Reveal>

      {/* ─── UNIQUE — OPEN HOUSE TOUR ──────────────────────────────── */}
      <section
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${warmDark}22`,
          background: `${cinnamon}08`,
          position: "relative",
        }}
      >
        <div style={{ maxWidth: 1040, margin: "0 auto" }}>
          <Reveal reduce={reduce} onShow={swooshOnShow}>
            <div style={{ textAlign: "center", marginBottom: 56 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: cinnamon,
                  marginBottom: 16,
                }}
              >
                {tt("variant.u01.tour.eyebrow")}
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  margin: 0,
                  color: warmDark,
                  letterSpacing: "-0.005em",
                }}
              >
                {tt("variant.u01.tour.title")}
              </h2>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  fontStyle: "italic",
                  color: `${warmDark}aa`,
                  marginTop: 16,
                  marginBottom: 0,
                }}
              >
                {tt("variant.u01.tour.subtitle")}
              </p>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: cinnamon,
                  margin: "20px auto 0",
                }}
              />
            </div>
          </Reveal>

          <div
            className="uy-tour-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 20,
            }}
          >
            {ROOMS.map((room, i) => {
              const name = tt(`variant.u01.tour.${i}.name`);
              const note = tt(`variant.u01.tour.${i}.note`);
              return (
                <Reveal key={room.key} reduce={reduce} delay={Math.min(i * 80, 320)}>
                  <article
                    className="uy-room-card"
                    style={{
                      width: "100%",
                      padding: "28px 22px",
                      background: cream,
                      border: `1px solid ${cinnamon}55`,
                      borderRadius: 6,
                      textAlign: "center",
                      minHeight: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 14,
                      color: "inherit",
                      font: "inherit",
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: `${cinnamon}15`,
                        border: `1px solid ${cinnamon}55`,
                        display: "grid",
                        placeItems: "center",
                      }}
                    >
                      <RoomIcon kind={room.key} color={cinnamon} />
                    </div>
                    <div
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 24,
                        fontWeight: 400,
                        color: warmDark,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-body)",
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: `${warmDark}aa`,
                        fontStyle: "italic",
                      }}
                    >
                      {note}
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── UNIQUE — HADIYALAR (Gifts wishlist) ───────────────────── */}
      <section
        style={{
          padding: "120px 32px",
          borderTop: `1px solid ${warmDark}22`,
          background: cream,
        }}
      >
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <Reveal reduce={reduce} onShow={swooshOnShow}>
            <div style={{ textAlign: "center", marginBottom: 48 }}>
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: cinnamon,
                  marginBottom: 16,
                }}
              >
                {tt("variant.u01.gifts.eyebrow")}
              </div>
              <h2
                style={{
                  fontFamily: t.font || "var(--font-display)",
                  fontStyle: "italic",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  fontWeight: 400,
                  margin: 0,
                  color: warmDark,
                  letterSpacing: "-0.005em",
                }}
              >
                {tt("variant.u01.gifts.title")}
              </h2>
              <p
                style={{
                  fontFamily: "var(--font-body)",
                  fontSize: 16,
                  fontStyle: "italic",
                  color: `${warmDark}aa`,
                  marginTop: 16,
                  marginBottom: 0,
                  maxWidth: 560,
                  marginInline: "auto",
                }}
              >
                {tt("variant.u01.gifts.subtitle")}
              </p>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: cinnamon,
                  margin: "20px auto 0",
                }}
              />
            </div>
          </Reveal>

          <div
            className="uy-gifts-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 0,
            }}
          >
            {Array.from({ length: GIFT_COUNT }).map((_, i) => {
              const name = tt(`variant.u01.gifts.${i}.name`);
              const price = tt(`variant.u01.gifts.${i}.price`);
              return (
                <Reveal key={i} reduce={reduce} delay={Math.min(i * 60, 240)}>
                  <button
                    type="button"
                    className="uy-gift-row"
                    onClick={() => play("click")}
                    style={{
                      width: "100%",
                      padding: "22px 20px",
                      borderTop: "none",
                      borderLeft: "none",
                      borderRight: "none",
                      borderBottom: `1px dashed ${cinnamon}55`,
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      alignItems: "baseline",
                      gap: 18,
                      cursor: "pointer",
                      background: "transparent",
                      color: "inherit",
                      textAlign: "left",
                      font: "inherit",
                    }}
                  >
                    <div
                      aria-hidden
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.18em",
                        color: cinnamon,
                        minWidth: 28,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      style={{
                        fontFamily: t.font || "var(--font-display)",
                        fontStyle: "italic",
                        fontSize: 22,
                        fontWeight: 400,
                        color: warmDark,
                        letterSpacing: "0.005em",
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: 13,
                        fontWeight: 600,
                        letterSpacing: "0.04em",
                        color: cinnamon,
                        fontFeatureSettings: '"tnum" 1, "lnum" 1',
                        whiteSpace: "nowrap",
                      }}
                    >
                      {price}
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 32,
              textAlign: "center",
              fontFamily: "var(--font-body)",
              fontSize: 14,
              fontStyle: "italic",
              color: `${warmDark}88`,
            }}
          >
            {tt("variant.u01.gifts.disclaimer")}
          </div>
        </div>
      </section>

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <GallerySection t={t} />
      </Reveal>

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <ScheduleSection
          t={t}
          items={[
            ["11:00", tt("variant.u01.schedule.0.event")],
            ["12:00", tt("variant.u01.schedule.1.event")],
            ["14:00", tt("variant.u01.schedule.2.event")],
            ["16:00", tt("variant.u01.schedule.3.event")],
            ["18:00", tt("variant.u01.schedule.4.event")],
          ]}
        />
      </Reveal>

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <MapSection
          t={t}
          isDark={isDark}
          addressLine={tt("variant.u01.map.address")}
        />
      </Reveal>

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedRsvp.setRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label={tt("variant.u01.rsvp.label")}
        />
      </Reveal>

      <Reveal reduce={reduce} onShow={swooshOnShow}>
        <ShareSection t={t} />
      </Reveal>

      <GuestFooter t={t} />

      {/* ─── Floating hearth music toggle ───────────────────────────── */}
      <button
        type="button"
        className="uy-music-btn uy-music-toggle"
        onClick={toggleMusic}
        aria-pressed={music}
        aria-label={music ? "Pause music" : "Play music"}
        style={{
          position: "fixed",
          right: 24,
          bottom: 24,
          zIndex: 50,
          width: 56,
          height: 56,
          borderRadius: "50%",
          border: `1px solid ${warmDark}`,
          background: music ? cinnamon : cream,
          color: music ? cream : warmDark,
          cursor: "pointer",
          display: "grid",
          placeItems: "center",
          fontFamily: "var(--font-ui)",
          fontSize: 18,
          boxShadow: `0 6px 14px ${warmDark}33`,
          padding: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
          {music ? (
            <g fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </g>
          ) : (
            <g fill="currentColor">
              <path d="M12 4 L18 3 L18 14 A3 3 0 1 1 16 11 L16 6 L14 6.4 L14 16 A3 3 0 1 1 12 13 Z" />
            </g>
          )}
        </svg>
      </button>
    </div>
  );
}
